import {
  Box,
  Card,
  Typography,
  TableHead,
  CardMedia,
  Tooltip,
  IconButton,
  lighten,
  TableRow,
  TableCell,
  TableBody,
  Avatar,
  Table,
  TableContainer,
  styled,
  useTheme,
  Tab
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import CloseTwoToneIcon from '@mui/icons-material/CloseTwoTone';
import TrendingDownTwoToneIcon from '@mui/icons-material/TrendingDownTwoTone';
import TrendingUpTwoToneIcon from '@mui/icons-material/TrendingUpTwoTone';
import TrendingFlatTwoToneIcon from '@mui/icons-material/TrendingFlatTwoTone';
import Text from 'src/components/Text';
import { format, subDays } from 'date-fns';
import moment from 'moment';

const CardActions = styled(Box)(
  ({ theme }) => `
    position: absolute;
    right: ${theme.spacing(2)};
    top: ${theme.spacing(2)};
    z-index: 7;
  `
);

const LabelInfo = styled(Box)(
  ({ theme }) => `
    display: inline-block;
    background: ${theme.palette.info.main};
    color: ${theme.palette.info.contrastText};
    text-transform: uppercase;
    font-size: ${theme.typography.pxToRem(10)};
    font-weight: bold;
    line-height: 23px;
    height: 22px;
    padding: ${theme.spacing(0, 2)};
    border-radius: ${theme.general.borderRadiusSm};
  `
);

const TableHeadWrapper = styled(TableHead)(
  ({ theme }) => `
      .MuiTableCell-root {
          text-transform: none;
          font-size: ${theme.typography.pxToRem(15)};
          padding-top: 0;
          padding-bottom: ${theme.spacing(1)};
          color: ${theme.colors.alpha.black[100]};
      }

      .MuiTableRow-root {
          background: transparent;
      }
  `
);

const TableWrapper = styled(Table)(
  () => `
    .MuiTableCell-root {
        border-bottom: 0;
    }
  `
);

const IconButtonWrapper = styled(IconButton)(
  ({ theme }) => `
    transition: ${theme.transitions.create(['transform', 'background'])};
    transform: scale(1);
    transform-origin: center;

    &:hover {
        transform: scale(1.1);
    }
  `
);

const DotLegend = styled('span')(
  ({ theme }) => `
      border-radius: 22px;
      width: 10px;
      height: 10px;
      display: inline-block;
      margin-right: ${theme.spacing(0.5)};
  `
);

interface Transaction {
  appName?: string;
  appRegisterId: string;
  employee?: { name?: string; id?: string };
  batchNumber?: string;
  transactionType?: string;
  remark?: string;
  note: string;
  createdOn: Date;
  tenantCode?: string;
  amount?: number;
}

interface BatchTransactionProps {
  transaction: Transaction[] | null | undefined;
}

function BatchTransaction({ transaction }: BatchTransactionProps) {
  const { t }: { t: any } = useTranslation();
  const theme = useTheme();

  return (
    <Card>
      <Box
        sx={{
          position: 'relative'
        }}
      >
        <CardMedia
          component="img"
          height="222"
          image="/static/images/Dashboard/header1.png"
          alt="..."
        />
        <CardActions>
          <LabelInfo
            sx={{
              borderRadius: 50
            }}
          >
            {t('Batch Transactions')}
          </LabelInfo>
        </CardActions>
      </Box>
      <Box p={2}>
        <TableContainer>
          <TableWrapper>
            <TableHeadWrapper>
              <TableRow>
                <TableCell>{t('Date Time')}</TableCell>
                <TableCell>{t('App Name')}</TableCell>
                <TableCell>{t('Batch Number')}</TableCell>
                <TableCell>{t('Employee')}</TableCell>
                <TableCell>{t('Type')}</TableCell>
                <TableCell>{t('Amount')}</TableCell>
                <TableCell>{t('Remark')}</TableCell>
                <TableCell>{t('Note')}</TableCell>
              </TableRow>
            </TableHeadWrapper>
            <TableBody>
              {transaction && transaction.length > 0 ? (
                transaction.map((txn, idx) => (
                  <TableRow hover key={idx}>
                    <TableCell>
                      {txn.createdOn ? moment(txn.createdOn).format('YYYY-MM-DD HH:mm:ss') : '--'}
                    </TableCell>
                    <TableCell>
                      {txn.appName || '--'}
                    </TableCell>
                    <TableCell>
                    {txn.batchNumber || '--'}
                    </TableCell>
                    <TableCell>
                      {txn.employee?.name || '--'}
                    </TableCell>
                    <TableCell>
                      {txn.transactionType || '--'}
                    </TableCell>
                    <TableCell>
                      {txn.amount !== undefined ? txn.amount : '--'}
                    </TableCell>
                    <TableCell>
                      {txn.remark || '--'}
                    </TableCell>
                    <TableCell>
                      {txn.note || '--'}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    {t('No transactions found')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </TableWrapper>
        </TableContainer>
      </Box>
    </Card>
  );
}

export default BatchTransaction;
